import {EditorView} from '@codemirror/view'
import type {ContenteditableElement, ShowSuggestionsEvent, Trigger} from '@github-ui/inline-autocomplete/types'
import {calculateSuggestionsQuery} from '@github-ui/inline-autocomplete/utils'
import type {SetStateAction} from 'react'

/** Small margin adjustments to make CodeMirror's coordinates API match up better visually with the positioning calculated via
 * `DOMInputRange` in the classic markdown editor. */
const autocompleteTopPositioningMargin = 5
const autocompleteLeftPositioningMargin = 10

/** Our own autocomplete extension for CodeMirror so we can use the UI in the existing `AutocompleteSuggestions`
 * component; the built-in autocomplete looks super different. */

export const autocompleteListenerExtension = (
  setSuggestionEvent: (value: SetStateAction<ShowSuggestionsEvent | null>) => void,
  setAutocompleteVisible: (value: SetStateAction<boolean>) => void,
  setTriggerCharCoords: (
    value: SetStateAction<{
      top: number
      left: number
      height: number
    }>,
  ) => void,
  triggers: Trigger[],
  showEventRef: React.MutableRefObject<ShowSuggestionsEvent | null>,
) => {
  const triggerCharacters = triggers.map(t => t.triggerChar)
  return EditorView.updateListener.of(update => {
    if (!update.docChanged) return
    const docString = update.state.doc.toString()
    let autocompleteTriggerCharFound = false
    for (const transaction of update.transactions) {
      // Ignore non-direct-typing changes
      if (
        !transaction.isUserEvent('input.type') &&
        !transaction.isUserEvent('delete.backward') &&
        !transaction.isUserEvent('delete.forward') &&
        !transaction.isUserEvent('undo') &&
        !transaction.isUserEvent('redo')
      )
        continue
      let triggeringChar = ''
      const newSelection = update.state.selection.main.anchor
      // Why consult the full new doc string instead of iterating through all changes in the transaction? We want to
      // consider all of a transaction's changes as a whole, so we neither pop up multiple autocompletes nor show an
      // autocomplete if a later change undoes a previous one in the changeset.
      for (const char of triggerCharacters) {
        if (!autocompleteTriggerCharFound && docString.lastIndexOf(char) === newSelection - 1) {
          autocompleteTriggerCharFound = true
          triggeringChar = char
        }
      }

      const suggestionQuery = calculateSuggestionsQuery(
        triggers,
        docString,
        update.view.state.selection.main.anchor ?? 0,
      )

      const contenteditableNode = update.view.contentDOM as ContenteditableElement
      showEventRef.current = suggestionQuery && {
        ...suggestionQuery,
        target: contenteditableNode,
      }
      // Recalculate suggestions when typing
      setSuggestionEvent({
        query: suggestionQuery?.query ?? '',
        trigger: {triggerChar: suggestionQuery?.trigger.triggerChar ?? ''},
        target: contenteditableNode,
      })

      setAutocompleteVisible(showEventRef.current !== null)

      if (autocompleteTriggerCharFound) {
        const caretVisualPosition = update.view.coordsAtPos(update.view.state.selection.main.anchor - 1, 1)

        const topPosition = caretVisualPosition ? caretVisualPosition.top + autocompleteTopPositioningMargin : 0
        const leftPosition = caretVisualPosition ? caretVisualPosition.left + autocompleteLeftPositioningMargin : 0

        setSuggestionEvent({
          query: suggestionQuery?.query ?? '',
          trigger: {triggerChar: triggeringChar},
          target: contenteditableNode,
        })
        setTriggerCharCoords({
          top: topPosition,
          left: leftPosition,
          height: caretVisualPosition ? caretVisualPosition.bottom - caretVisualPosition.top : 0,
        })
      }
    }
  })
}
